"use client";

import Image from "next/image";
import {
  ExternalLink,
  Edit3,
  Loader2,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";
import {
  useFetchProjectsMutation,
  useFetchProjectInfoMutation,
  useGetDisplayAppsQuery,
  useFetchAppStatusMutation,
} from "@/services/chatApi";
import {
  useFetchAppPreviewMutation,
  useDeployAppMutation,
} from "@/services/deployApi";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { setAppPreview } from "../../store/appPreviewSlice";
import { setDesignData } from "@/store/designSlice";
import { setProjectInfo } from "@/store/projectInfoSlice";
import { RootState } from "../../store";
import {
  setAllVersions,
  setCurrentVersion,
} from "@/store/versionSlice";

type ProjectItem = {
  id: string;
  name: string;
  category: string;
  image: string;
  project_type?: string;
};

type DisplayAppItem = {
  app_url: string;
  app_name: string;
  app_project_id: string;
};



const SkeletonCard = () => (
  <div className="overflow-hidden rounded-lg border border-border bg-card animate-pulse">
    <div className="aspect-video bg-muted" />
    <div className="p-4">
      <div className="mb-2 h-4 w-2/3 rounded bg-muted" />
      <div className="h-3 w-1/2 rounded bg-muted" />
    </div>
  </div>
);

const openExternal = (url: string) =>
  window.open(url, "_blank", "noopener,noreferrer");

const PortfolioGrid = () => {
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] =
    useState<ProjectItem | null>(null);
  const [selectedDisplayApp, setSelectedDisplayApp] =
    useState<DisplayAppItem | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeDisplayCategoryIndex, setActiveDisplayCategoryIndex] =
    useState(0);
  const [carouselDirection, setCarouselDirection] =
    useState<1 | -1>(1);
  const displayPreviewContainerRef = useRef<HTMLDivElement | null>(null);
  const [displayPreviewScale, setDisplayPreviewScale] = useState(1);
  const [displayPreviewHeight, setDisplayPreviewHeight] = useState(0);

  const [fetchProjects] = useFetchProjectsMutation();
  const [fetchAppPreview] = useFetchAppPreviewMutation();
  const [fetchAppStatus] = useFetchAppStatusMutation();
  const [deployApp] = useDeployAppMutation();
  const [fetchProjectInfo] = useFetchProjectInfoMutation();
  const {
    data: displayAppsResponse,
    isLoading: displayAppsLoading,
    isFetching: displayAppsFetching,
  } = useGetDisplayAppsQuery();
  const dispatch = useDispatch();
  const router = useRouter();
  const selectedVersion = useSelector(
    (state: RootState) => state.version.currentVersion
  );

  const ITEMS_PER_PAGE = 15;
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const displayAppSections = useMemo(() => {
    const response = displayAppsResponse as
      | Record<string, any>
      | undefined;

    return (
      (response?.display_apps as Record<
        string,
        DisplayAppItem[]
      >) ||
      (response?.data?.display_apps as Record<
        string,
        DisplayAppItem[]
      >) ||
      (response?.displayApps as Record<
        string,
        DisplayAppItem[]
      >) ||
      {}
    );
  }, [displayAppsResponse]);

  const displayCategories = useMemo(
    () => Object.keys(displayAppSections),
    [displayAppSections]
  );

  const activeDisplayCategory =
    displayCategories[activeDisplayCategoryIndex] || "";

  const activeDisplayApps = useMemo(() => {
    return (
      displayAppSections[activeDisplayCategory]?.slice(0, 3) ||
      []
    );
  }, [displayAppSections, activeDisplayCategory]);

  const filteredProjects = projects.filter((project) => {
    if (!normalizedQuery) return true;
    const title = project.name?.toLowerCase() ?? "";
    const description =
      project.category?.toLowerCase() ?? "";
    return (
      title.includes(normalizedQuery) ||
      description.includes(normalizedQuery)
    );
  });

  const totalPages = Math.ceil(
    filteredProjects.length / ITEMS_PER_PAGE
  );

  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const renderHighlightedText = (text: string) => {
    if (!normalizedQuery) return text;
    const escapedQuery = normalizedQuery.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );
    const regex = new RegExp(`(${escapedQuery})`, "ig");
    return text.split(regex).map((part, index) =>
      part.toLowerCase() === normalizedQuery ? (
        <mark
          key={`${part}-${index}`}
          className="rounded-md bg-[rgba(235,99,139,0.18)] px-1 py-0.5 text-[#ffd7e3]"
        >
          {part}
        </mark>
      ) : (
        <span key={`${part}-${index}`}>{part}</span>
      )
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (
      activeDisplayCategoryIndex >
      Math.max(displayCategories.length - 1, 0)
    ) {
      setActiveDisplayCategoryIndex(0);
    }
  }, [displayCategories, activeDisplayCategoryIndex]);

  useEffect(() => {
    if (displayCategories.length <= 1) return;
    const interval = setInterval(() => {
      setCarouselDirection(1);
      setActiveDisplayCategoryIndex((current) => {
        return (current + 1) % displayCategories.length;
      });
    }, 12000);
    return () => clearInterval(interval);
  }, [displayCategories]);

  useEffect(() => {
    if (!selectedDisplayApp) return;

    const container = displayPreviewContainerRef.current;
    if (!container) return;

    const DESKTOP_WIDTH = 1440;
    const DESKTOP_HEIGHT = 900;

    const updateScale = () => {
      const availableWidth = container.clientWidth;
      const availableHeight = container.clientHeight;
      if (!availableWidth || !availableHeight) return;

      const widthBasedScale = Math.min(
        availableWidth / DESKTOP_WIDTH,
        1
      );
      const scaledHeight = DESKTOP_HEIGHT * widthBasedScale;

      setDisplayPreviewScale(widthBasedScale);
      setDisplayPreviewHeight(
        Math.min(scaledHeight, availableHeight)
      );
    };

    updateScale();

    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [selectedDisplayApp]);

  const authUser = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (!authUser?.uid) {
      setProjects([]);
      setLoading(false);
      return;
    }

    const loadProjects = async () => {
      setLoading(true);
      try {
        const response = await fetchProjects({
          user_id: authUser.uid,
        }).unwrap();
        if (response.success && response.projects?.length > 0) {
          const formatted = response.projects.map((p: any) => ({
            id: p.project_id,
            name: p.project_name,
            category: p.project_description,
            image: p.screenshot_url_dev,
            project_type: p.project_type,
          }));
          setProjects(formatted);
        } else {
          setProjects([]);
        }
      } catch (err) {
        console.error("Failed to fetch projects:", err);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [authUser?.uid, fetchProjects]);

  const getOrDeployPreview = async (projectId: string) => {
    const previewResponse = await fetchAppPreview({
      app_project_id: projectId,
      version_id: selectedVersion,
    }).unwrap();
    if (previewResponse?.preview_url) return previewResponse;

    return await deployApp({
      app_project_id: projectId,
      version_id: selectedVersion,
    }).unwrap();
  };

  const getPreviewForEdit = async (projectId: string) => {
    const statusResponse = await fetchAppStatus({
      app_project_id: projectId,
    }).unwrap();

    const projectInfo = await fetchProjectInfo({
      project_id: projectId,
    }).unwrap();
    dispatch(setProjectInfo(projectInfo));
    dispatch(setCurrentVersion(projectInfo.current_version));
    const versions = projectInfo["all-app-versions"]?.map(
      (v: any) => v.version
    );
    dispatch(setAllVersions(versions));

    return statusResponse;
  };

  const handleEdit = async (project: ProjectItem) => {
    setIsProcessing(true);
    try {
      const finalPreviewResponse = await getPreviewForEdit(
        project.id
      );
      dispatch(setAppPreview(finalPreviewResponse));
      dispatch(
        setDesignData({
          app_project_id: project.id,
          project_name: project.name,
          project_description: project.category,
        })
      );
      router.push(`/project/${project.id}`);
    } catch (err) {
      console.error(
        "Error fetching or deploying app preview:",
        err
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExternalLink = async (project: ProjectItem) => {
    setIsProcessing(true);
    try {
      const finalPreviewResponse = await getOrDeployPreview(
        project.id
      );
      if (finalPreviewResponse?.preview_url) {
        openExternal(finalPreviewResponse.preview_url);
      }
    } catch (err) {
      console.error(
        "Error fetching or deploying app preview:",
        err
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="relative mx-auto w-full max-w-7xl px-2 py-6 md:px-4 md:py-12">
      {(displayAppsLoading ||
        displayAppsFetching ||
        displayCategories.length > 0) && (
          <section className="mb-14">
            <div className="flex flex-wrap gap-2">
              {displayCategories.map((category) => {
                const isActive =
                  category === activeDisplayCategory;

                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => {
                      const nextIndex =
                        displayCategories.indexOf(category);
                      setCarouselDirection(
                        nextIndex >= activeDisplayCategoryIndex
                          ? 1
                          : -1
                      );
                      setActiveDisplayCategoryIndex(nextIndex);
                    }}
                    className={`rounded-full px-4 py-2 text-xs font-medium transition ${isActive
                        ? "bg-gradient-to-r from-[#ac274f] to-[#eb638b] text-white shadow-[0_12px_30px_rgba(172,39,79,0.22)]"
                        : "border border-white/10 bg-white/5 text-zinc-300 hover:border-[#8d4b61] hover:text-white"
                      }`}
                  >
                    {category}
                  </button>
                );
              })}
            </div>

            {displayAppsLoading || displayAppsFetching ? (
              <div className="mt-6 grid gap-4 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className="overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.025))] animate-pulse"
                  >
                    <div className="aspect-[16/10] bg-white/5" />
                    <div className="space-y-3 p-4">
                      <div className="h-3 w-24 rounded bg-white/10" />
                      <div className="h-5 w-40 rounded bg-white/10" />
                    </div>
                  </div>
                ))}
              </div>
            ) : activeDisplayApps.length > 0 ? (
              <div className="mt-6 overflow-hidden">
                <AnimatePresence mode="wait" custom={carouselDirection}>
                  <motion.div
                    key={activeDisplayCategory}
                    custom={carouselDirection}
                    initial={{
                      x: carouselDirection > 0 ? 140 : -140,
                      opacity: 0,
                    }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{
                      x: carouselDirection > 0 ? -140 : 140,
                      opacity: 0,
                    }}
                    transition={{
                      duration: 0.42,
                      ease: "easeInOut",
                    }}
                    className="grid gap-4 lg:grid-cols-3"
                  >
                    {activeDisplayApps.map((app, index) => (
                      <div
                        key={`${app.app_project_id}-${index}`}
                        className="group relative cursor-pointer overflow-hidden rounded-lg border border-border bg-card transition-all duration-300 hover:scale-[1.02] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedDisplayApp(app)
                          }
                          className="block w-full text-left"
                        >
                          <div className="relative aspect-video overflow-hidden bg-muted">
                            <iframe
                              src={app.app_url}
                              title={app.app_name}
                              className="absolute left-0 top-0 origin-top-left border-0"
                              style={{
                                width: "1680px",
                                height: "900px",
                                transform: "scale(0.245)",
                                pointerEvents: "none",
                              }}
                            />
                            <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/10" />
                          </div>
                        </button>

                        <div className="p-4">
                          <h3 className="mb-1 text-sm font-medium text-foreground transition-colors group-hover:text-foreground/80">
                            <span className="truncate">
                              {app.app_name}
                            </span>
                          </h3>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>
            ) : (
              <div className="mt-6 rounded-[24px] border border-dashed border-white/10 bg-white/5 px-5 py-10 text-center">
                <p className="text-sm text-zinc-400">
                  Featured apps are not available yet.
                </p>
              </div>
            )}
          </section>
        )}

      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            My Projects
          </h2>

          <div className="relative ml-10 w-[320px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/80" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search title or description"
              className="h-10 w-full rounded-full border border-border bg-card pl-10 pr-10 text-sm text-foreground outline-none transition focus:border-[#eb638b] focus:ring-1 focus:ring-[#eb638b]"
            />
            {searchQuery && (
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-zinc-400">
                {filteredProjects.length}
              </div>
            )}
          </div>
        </div>

        {!loading && totalPages > 1 && (
          <div className="flex items-center gap-3">
            <button
              onClick={() =>
                setCurrentPage((p) => Math.max(1, p - 1))
              }
              disabled={currentPage === 1}
              className="rounded-full border-2 border-[var(--color-gold)] px-4 py-1.5 text-xs font-semibold text-[var(--color-gold)] transition hover:bg-[var(--color-gold)] hover:text-black disabled:opacity-30"
            >
              Prev
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map(
                (_, i) => {
                  const page = i + 1;
                  const isActive = currentPage === page;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs transition ${isActive
                          ? "bg-gradient-to-r from-[#ac274f] to-[#eb638b] text-white"
                          : "border border-zinc-500 text-gray-200 hover:border-[var(--color-gold)] hover:text-[var(--color-gold)]"
                        }`}
                    >
                      {page}
                    </button>
                  );
                }
              )}
            </div>

            <button
              onClick={() =>
                setCurrentPage((p) =>
                  Math.min(totalPages, p + 1)
                )
              }
              disabled={currentPage === totalPages}
              className="rounded-full border-2 border-[var(--color-gold)] px-4 py-1.5 text-xs font-semibold text-[var(--color-gold)] transition hover:bg-[var(--color-gold)] hover:text-black disabled:opacity-30"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center rounded-xl bg-background/80 backdrop-blur-sm" />
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
        {loading
          ? Array.from({ length: 9 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))
          : paginatedProjects.map((item) => (
            <div
              key={item.id}
              className={`group relative cursor-pointer overflow-hidden rounded-lg border border-border bg-card transition-all duration-300 hover:scale-[1.02] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${isProcessing &&
                  selectedProject?.id !== item.id
                  ? "pointer-events-none opacity-60"
                  : ""
                }`}
              onClick={(e) => {
                if (
                  (e.target as HTMLElement).closest("button")
                ) {
                  return;
                }
                if (
                  isProcessing &&
                  selectedProject &&
                  selectedProject.id !== item.id
                ) {
                  return;
                }
                setSelectedProject(item);
              }}
            >
              <div className="relative aspect-video overflow-hidden bg-muted">
                <Image
                  className={`h-full w-full transition-transform duration-300 group-hover:scale-105 ${item.image && item.image.trim() !== ""
                      ? "object-cover object-top"
                      : "object-cover"
                    }`}
                  src={
                    item.image && item.image.trim() !== ""
                      ? item.image
                      : "/default.png"
                  }
                  alt={item.name || "Project preview"}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  unoptimized
                />
                <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/10" />
              </div>

              <div className="p-4">
                <h3 className="mb-1 flex items-center gap-2 text-sm font-medium text-foreground transition-colors group-hover:text-foreground/80">
                  <span className="truncate">
                    {renderHighlightedText(item.name)}
                  </span>
                  {item.project_type && (
                    <span className="mx-1 whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {String(item.project_type).replace(
                        "_",
                        " "
                      )}
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground opacity-70">
                  {renderHighlightedText(item.category)}
                </p>
              </div>
            </div>
          ))}
      </div>

      {!loading && filteredProjects.length === 0 && (
        <div className="mt-12 rounded-[24px] border border-dashed border-white/10 bg-white/5 px-5 py-16 text-center">
          <p className="text-sm text-zinc-400">
            {searchQuery
              ? "No projects matched your search."
              : "You haven't created any projects yet. Once a project is created, it will be displayed here."}
          </p>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="mt-10 flex justify-end">
          <div className="flex items-center gap-3">
            <button
              onClick={() =>
                setCurrentPage((p) => Math.max(1, p - 1))
              }
              disabled={currentPage === 1}
              className="rounded-full border-2 border-[var(--color-gold)] px-4 py-1.5 text-xs font-semibold text-[var(--color-gold)] transition hover:bg-[var(--color-gold)] hover:text-black disabled:opacity-30"
            >
              Prev
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map(
                (_, i) => {
                  const page = i + 1;
                  const isActive = currentPage === page;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs transition ${isActive
                          ? "bg-gradient-to-r from-[#ac274f] to-[#eb638b] text-white"
                          : "border border-zinc-500 text-gray-200 hover:border-[var(--color-gold)] hover:text-[var(--color-gold)]"
                        }`}
                    >
                      {page}
                    </button>
                  );
                }
              )}
            </div>

            <button
              onClick={() =>
                setCurrentPage((p) =>
                  Math.min(totalPages, p + 1)
                )
              }
              disabled={currentPage === totalPages}
              className="rounded-full border-2 border-[var(--color-gold)] px-4 py-1.5 text-xs font-semibold text-[var(--color-gold)] transition hover:bg-[var(--color-gold)] hover:text-black disabled:opacity-30"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedProject && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => {
            if (isProcessing) return;
            setSelectedProject(null);
          }}
        >
          <div
            className="relative flex max-h-[90vh] w-[90vw] max-w-5xl flex-col overflow-hidden rounded-xl bg-background shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/90 p-4 backdrop-blur-md">
              <h2 className="truncate pr-4 text-lg font-semibold text-foreground">
                {selectedProject.name}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    if (isProcessing) return;
                    setIsEditing(true);
                    setIsProcessing(true);
                    await handleEdit(selectedProject);
                    setIsEditing(false);
                    setIsProcessing(false);
                  }}
                  className={`flex items-center gap-2 rounded-full bg-gradient-to-r from-[#ac274f] to-[#eb638b] shadow-[0_18px_40px_rgba(172,39,79,0.32)] px-4 py-2 text-sm font-medium text-white transition hover:scale-[1.02] cursor-pointer ${isProcessing
                      ? "pointer-events-none opacity-70"
                      : ""
                    }`}
                >
                  {isEditing ? (
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  ) : (
                    <Edit3
                      className="h-4 w-4 text-white"
                      strokeWidth={2.5}
                    />
                  )}
                  <span className="text-white font-medium">Edit</span>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <div className="relative h-auto w-full">
                <Image
                  src={
                    selectedProject.image &&
                      selectedProject.image.trim() !== ""
                      ? selectedProject.image
                      : "/default.png"
                  }
                  alt={
                    selectedProject.name || "Project preview"
                  }
                  width={1920}
                  height={1080}
                  unoptimized
                  className="h-auto w-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedDisplayApp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedDisplayApp(null)}
        >
          <div
            className="relative flex h-[90vh] w-[94vw] max-w-6xl flex-col overflow-hidden rounded-xl bg-background shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/90 p-4 backdrop-blur-md">
              <h2 className="truncate pr-4 text-lg font-semibold text-foreground">
                {selectedDisplayApp.app_name}
              </h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    openExternal(selectedDisplayApp.app_url)
                  }
                  className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#ac274f] to-[#eb638b] shadow-[0_18px_40px_rgba(172,39,79,0.32)] px-4 py-2 text-sm font-medium text-white transition hover:scale-[1.02] cursor-pointer"
                >
                  <ExternalLink
                    className="h-4 w-4 text-white"
                    strokeWidth={2.5}
                  />
                  <span className="text-white font-medium">View</span>
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0">
              <div
                ref={displayPreviewContainerRef}
                className="relative flex h-full w-full items-start justify-start overflow-hidden bg-black"
              >
                <div
                  className="relative w-full overflow-hidden bg-white"
                  style={{
                    height: displayPreviewHeight
                      ? `${displayPreviewHeight}px`
                      : undefined,
                  }}
                >
                  <iframe
                    src={selectedDisplayApp.app_url}
                    title={selectedDisplayApp.app_name}
                    className="absolute left-0 top-0 origin-top-left border-0 bg-white custom-scrollbar"
                    style={{
                      width: "1440px",
                      height: "900px",
                      transform: `scale(${displayPreviewScale})`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioGrid;
